import type { CrawlerFormData, ScheduleFormValue } from "pages/crawler/types";
import type { CostCalculationProject, OutputFormat } from "providers/HostedScrapingProvider/types";
import { buildCostProject } from "utils/buildCostProject";

type CronOptions = "once" | "disabled" | "hourly" | "daily" | "weekly" | "monthly";

type SchedulePreset = {
  label: string;
  cron: string;
  enabled: boolean;
};

const CRON_OPTIONS: CronOptions[] = [
  "once",
  "disabled",
  "hourly",
  "daily",
  "weekly",
  "monthly",
];

const DEFAULT_CRON_OPTION: CronOptions = "daily";

export const CRON_PRESETS: Record<CronOptions, SchedulePreset> = {
  once: { label: "Run crawler job once (now)", cron: "", enabled: true },
  disabled: { label: "Do not schedule", cron: "", enabled: false },
  hourly: { label: "Hourly", cron: "0 * * * *", enabled: true },
  daily: { label: "Daily", cron: "0 0 * * *", enabled: true },
  weekly: { label: "Weekly", cron: "0 0 * * 0", enabled: true },
  monthly: { label: "Monthly", cron: "0 0 1 * *", enabled: true },
};

const ALL_INTERVALS = CRON_OPTIONS;

const toInterval = (raw?: string): CronOptions => {
  if (!raw) return DEFAULT_CRON_OPTION;
  const value = raw.toLowerCase().trim();
  if (value === "scheduling_disabled") return "disabled";
  const direct = ALL_INTERVALS.find((interval) => interval === value);
  if (direct) return direct;
  const byLabel = ALL_INTERVALS.find(
    (interval) => CRON_PRESETS[interval].label.toLowerCase() === value
  );
  return byLabel ?? DEFAULT_CRON_OPTION;
};

const cronString = (cron?: string): string => (cron ?? "").toString().trim();

const intervalFromCron = (cron?: string): CronOptions => {
  const cleanedCron = cronString(cron);
  if (!cleanedCron) return "once";

  // First try exact match against presets
  const found = ALL_INTERVALS.find((interval) => CRON_PRESETS[interval].cron === cleanedCron);
  if (found) return found;

  // Parse cron pattern to determine interval
  // Format: minute hour dayOfMonth month dayOfWeek
  const parts = cleanedCron.split(' ');
  if (parts.length !== 5) return DEFAULT_CRON_OPTION;

  const [minute, hour, dayOfMonth, , dayOfWeek] = parts;

  // Monthly: specific day of month, any day of week
  if (dayOfMonth !== '*' && dayOfWeek === '*') return 'monthly';
  // Weekly: specific day of week
  if (dayOfWeek !== '*') return 'weekly';
  // Daily: specific hour, any day
  if (hour !== '*' && dayOfMonth === '*') return 'daily';
  // Hourly: specific minute only
  if (minute !== '*' && hour === '*') return 'hourly';

  return DEFAULT_CRON_OPTION;
};

export const cleanSchedule = (schedule?: Partial<ScheduleFormValue> | null): ScheduleFormValue => {
  if (!schedule) {
    const preset = CRON_PRESETS[DEFAULT_CRON_OPTION];
    return { interval: DEFAULT_CRON_OPTION, cron: preset.cron, enabled: preset.enabled };
  }

  const interval = toInterval(schedule.interval);
  const trimmedName = schedule.name?.trim();
  const isDisabled = schedule.enabled === false || interval === "disabled";
  const baseInterval = isDisabled ? "disabled" : interval;
  const preset = CRON_PRESETS[baseInterval];

  return {
    interval: baseInterval,
    cron: isDisabled ? "" : cronString(schedule.cron ?? preset.cron),
    enabled: isDisabled ? false : schedule.enabled ?? preset.enabled,
    ...(trimmedName ? { name: trimmedName } : {}),
  };
};

const deriveScheduleFromBackend = (crawler: any): ScheduleFormValue => {
  const additionalData = crawler?.additionalData || {};
  const backendSchedule = crawler?.schedule || {};
  const forcedDisabled = crawler?.enabled === false;

  const rawInterval = backendSchedule.interval ?? additionalData.frequency;
  const interval = forcedDisabled
    ? "disabled"
    : rawInterval
    ? toInterval(rawInterval)
    : intervalFromCron(crawler?.cron);

  return cleanSchedule({
    interval,
    cron: backendSchedule.cron ?? crawler?.cron,
    name: backendSchedule.name ?? crawler?.name,
    enabled: forcedDisabled ? false : backendSchedule.enabled,
  });
};

const buildSchedulePayload = (schedule: ScheduleFormValue) => ({
  interval: schedule.interval,
  cron: schedule.cron,
  ...(schedule.name ? { name: schedule.name } : {}),
});

export const getFrequencyOptions = (): string[] =>
  ALL_INTERVALS.map((interval) => CRON_PRESETS[interval].label);

export const getScheduleFromDisplayName = (displayName: string): ScheduleFormValue => {
  const label = displayName.toLowerCase().trim();
  const match = ALL_INTERVALS.find((interval) => CRON_PRESETS[interval].label.toLowerCase() === label);
  return cleanSchedule({ interval: match });
};

export const getScheduleDisplayName = (schedule?: Partial<ScheduleFormValue> | null): string => {
  const cleaned = cleanSchedule(schedule);
  const interval = cleaned.interval as CronOptions;
  return CRON_PRESETS[interval]?.label ?? '';
};

export const formatRequiresAutoparse = (format?: string | null): boolean => {
  if (!format) return false;
  const formatted = format.toLowerCase();
  return formatted === "json" || formatted === "csv";
};

/**
 * Maps form data to the backend crawler data format
 */
export const mapFormDataToCrawlerData = (data: CrawlerFormData, crawlerName: string) => {
  // Schedule configuration
  const schedule = cleanSchedule(data.schedule);
  const name = (schedule.name || crawlerName).trim();
  const isDisabled = schedule.interval === "disabled";
  const enabled = !isDisabled && schedule.enabled !== false;
  const cron = isDisabled ? "" : schedule.cron;

  // URL pattern configuration
  const includePattern = data.includePath?.trim() || ".*";
  const excludePattern = data.excludePath?.trim() || null;

  const schedulePayload = buildSchedulePayload({
    ...schedule,
    ...(name ? { name } : {}),
    cron,
  });

  const sessionNumber = data.sessionNumber ? parseInt(String(data.sessionNumber), 10) : undefined;
  let outputFormat = data.outputFormat?.toLowerCase();
  const shouldAutoparse = formatRequiresAutoparse(outputFormat);

  if (outputFormat === "json") {
    outputFormat = '';
  }

  // Handle maxDepth: use provided value, or default to 2 if not set
  const maxDepth = data.linkDepth && data.linkDepth >= 1 ? parseInt(String(data.linkDepth)) : 2;

  // Handle crawlBudget: use provided value, or default to 1000 if not set
  const crawlBudget = data.maxCost && data.maxCost.trim() !== "" && parseInt(data.maxCost) >= 1 ? parseInt(String(data.maxCost)) : 1000;

  return {
    name,
    startUrl: data.url,
    maxDepth,
    urlRegexpInclude: includePattern,
    urlRegexpExclude: excludePattern,
    webhookUrl: data.callbackUrl || null,
    crawlBudget,
    cron,
    enabled,
    supposedToRunAt: undefined,
    schedule: schedulePayload,
    apiParams: {
      ...(data.deviceType && { device_type: data.deviceType.toLowerCase() }),
      ...(data.countryCode && { country_code: String(data.countryCode).toUpperCase() }),
      ...(sessionNumber !== undefined && !isNaN(sessionNumber) && { session_number: sessionNumber }),
      ...(data.useOwnHeaders && { keep_headers: true }),
      ...(data.advancedBypassing && { ultra_premium: true }),
      ...(data.residentialIps && { premium: true }),
      ...(data.renderJs && { render: true }),
      ...(data.screenshot && { screenshot: true }),
      ...(!data.disableRedirects && { follow_redirect: true }),
      ...(data.retry404 && { retry_404: true }),
      ...(outputFormat && { output_format: outputFormat }),
      ...(shouldAutoparse ? { autoparse: true } : {}),
    },
    additionalData: {
      notification: data.notification,
      dashboard_base_url: process.env.REACT_APP_DOMAIN,
    }
  };
};

/**
 * Maps backend crawler data to form data format
 */
export const mapCrawlerDataToFormData = (crawler: any): CrawlerFormData => {
  const apiParams = crawler.apiParams || {};
  const additionalData = crawler.additionalData || {};
  const backendOutputFormat = apiParams.output_format || apiParams.format || apiParams.outputFormat;
  const formattedOutputFormat = backendOutputFormat?.toLowerCase() || '';
  const derivedAutoparse =
    apiParams.autoparse !== undefined ? apiParams.autoparse : formatRequiresAutoparse(backendOutputFormat) ? true : undefined;

  const schedule = deriveScheduleFromBackend({
    ...crawler,
    additionalData,
    schedule: crawler.schedule,
  });

  // URL pattern configuration - backward compatibility with old field names
  const includePattern = crawler.urlRegexpInclude ?? crawler.urlPattern ?? ".*";
  const excludePattern = crawler.urlRegexpExclude ?? additionalData.excludePath ?? "";

  return {
    url: crawler.startUrl || '',
    linkDepth: crawler.maxDepth !== undefined && crawler.maxDepth !== null ? crawler.maxDepth : 2,
    includePath: includePattern,
    excludePath: excludePattern,
    callbackUrl: crawler.webhookUrl || '',
    maxCost: crawler.crawlBudget !== undefined && crawler.crawlBudget !== null ? crawler.crawlBudget.toString() : '1000',
    schedule,
    notification: additionalData.notification || 'never',
    outputFormat: formattedOutputFormat,
    autoparse: derivedAutoparse,
    disableRedirects: !apiParams.follow_redirect,
    retry404: (apiParams.retry_404 ?? apiParams.retry404) || false,
    renderJs: apiParams.render || false,
    screenshot: apiParams.screenshot || false,
    useOwnHeaders: apiParams.keep_headers || false,
    advancedBypassing: apiParams.ultra_premium || false,
    residentialIps: apiParams.premium || false,
    sessionNumber: apiParams.session_number !== undefined ? apiParams.session_number : undefined,
    countryCode: (apiParams.country_code || 'US').toString().toUpperCase(),
    deviceType: (apiParams.device_type || apiParams.deviceType || 'desktop').charAt(0).toUpperCase() + (apiParams.device_type || apiParams.deviceType || 'desktop').slice(1)
  };
};

/**
 * Gets the output format from API params, handling both old and new field names
 */
export const getOutputFormat = (apiParams: any): string => {
  const raw = apiParams?.output_format || apiParams?.format || apiParams?.outputFormat || 'json';
  return String(raw).toUpperCase();
};

/**
 * Extracts error message from API error response
 */
export const getErrorMessage = (error: any, fallback = 'An error occurred'): string => {
  return error?.response?.data?.message || error?.message || fallback;
};

export const crawlerFormToCostProject = (data: CrawlerFormData): CostCalculationProject | undefined => {
  if (!data.url?.trim()) return undefined;

  const apiParams: Record<string, unknown> = {};
  if (data.advancedBypassing) apiParams.ultraPremium = true;
  if (data.residentialIps) apiParams.premium = true;
  if (data.renderJs) apiParams.render = true;
  if (data.screenshot) apiParams.screenshot = true;
  if (data.disableRedirects) apiParams.followRedirect = false;
  if (data.retry404) apiParams.retry404 = true;
  if ((data.outputFormat || '').toLowerCase() === 'csv') apiParams.outputFormat = 'csv' as OutputFormat;
  const deviceType = (data.deviceType || '').toLowerCase();
  if (deviceType && deviceType !== 'desktop') apiParams.deviceType = deviceType;
  if (data.countryCode && data.countryCode.toUpperCase() !== 'US') apiParams.countryCode = data.countryCode;
  if (data.sessionNumber !== undefined) apiParams.sessionNumber = data.sessionNumber;
  if (data.useOwnHeaders) apiParams.keepHeaders = true;

  return buildCostProject({
    name: 'temp-crawler',
    input: { type: 'list_literal', list: data.url },
    config: { type: 'async_urls', apiParams },
  });
};
